import gql from 'graphql-tag';
import {
  SUCCESS_SMS_STATUSES,
  SENDING_SMS_STATUSES,
  DIRECT_SND_SMS_SUCCESS_STATUSES,
  DIRECT_SND_SMS_SUBMITTED_STATUSES,
  BILLING_SUCCESS_SMS_STATUSES,
  BILLING_SENDING_SMS_STATUSES,
  CANCELED_STATUS,
  CANCELED_DIRECT_SND_SMS_STATUS,
  FINISHED_SMS_STATUSES,
  FINISHED_DIRECT_SND_SMS_STATUSES,
  SUCCESS_SURVEY_STATUSES,
  FAILED_SURVEY_STATUSES,
  SUCCESS_CALL_STATUSES,
  FAILED_CALL_STATUSES,
  UNANSWERED_CALL_STATUSES,
  FINISHED_CALL_STATUSES,
} from 'src/constants/misc';
import { VotingAreaFields } from '../voting_areas/queries';

// sms_histories carries two status columns (Twilio `status`, DirectSND `direct_snd_status`) and the app
// reads them two different ways. Both shapes are kept so the counts below stay identical to what the
// client-side filters they replace produced.

// `A.includes(status) || B.includes(direct_snd_status)` — a NULL column is never matched by _in.
const eitherStatusIn = (statuses, directSndStatuses) =>
  `{ _or: [{ status: { _in: ${JSON.stringify(statuses)} } }, { direct_snd_status: { _in: ${JSON.stringify(
    directSndStatuses
  )} } }] }`;

// `list.includes(direct_snd_status ?? status)` — `??` falls through only on NULL.
const coalescedStatusIn = (list) =>
  `{ _or: [
        { direct_snd_status: { _in: ${JSON.stringify(list)} } },
        { _and: [{ direct_snd_status: { _is_null: true } }, { status: { _in: ${JSON.stringify(
          list
        )} } }] }
      ] }`;

// `!list.includes(direct_snd_status ?? status) && (direct_snd_status || status)`. The _is_null guards are
// required: SQL `NOT IN` evaluates to NULL (not TRUE) for a NULL column, which would drop rows that the JS
// filter counted. `''` joins the exclusion list to stand in for the JS truthiness check.
const coalescedStatusNotIn = (list) => {
  const excluded = JSON.stringify([...list, '']);
  return `{ _or: [
        { _and: [{ direct_snd_status: { _is_null: false } }, { direct_snd_status: { _nin: ${excluded} } }] },
        { _and: [
            { direct_snd_status: { _is_null: true } }
            { status: { _is_null: false } }
            { status: { _nin: ${excluded} } }
          ] }
      ] }`;
};

const smsCount = (alias, where) =>
  `${alias}: tasks_sms_histories_aggregate(where: ${where}) { aggregate { count } }`;

const TaskFragment = gql`
  fragment TaskFragment on ri_apps_prd_tasks {
    task_id
    is_paused
    is_archived
    name
    send_at
    is_bulk_task
    is_manual_number_task
    bulk_task_id
    start_date
    end_date

    children_in_house
    veteran
    absentee_outstanding
    exclude_early_voters

    preceding_task_id
    text_message
    calling_script
    sms_survey_id

    tasks_task_campaigns {
      task_campaign_id
      task_campaigns_campaign {
        campaign_id
        name
      }
    }
    tasks_task_types {
      task_type_id
      task_types_t_type {
        t_type_id
        name
        code
      }
    }
    tasks_manual_text_batches {
      manual_text_batch_id
      send_at
      total_count: manual_text_batch_manual_texts_aggregate {
        aggregate {
          count
        }
      }
      sent_count: manual_text_batch_manual_texts_aggregate(
        where: { _or: [{ status: { _in: ${JSON.stringify(
          SUCCESS_SMS_STATUSES
        )} } }, { direct_snd_status: { _in: ${JSON.stringify(
  DIRECT_SND_SMS_SUCCESS_STATUSES
)} } }] }
      ) {
        aggregate {
          count
        }
      }
      sending_count: manual_text_batch_manual_texts_aggregate(
        where: { _or: [{ status: { _in: ${JSON.stringify(
          SENDING_SMS_STATUSES
        )} } }, { direct_snd_status: { _in: ${JSON.stringify(
  DIRECT_SND_SMS_SUBMITTED_STATUSES
)} } }] }
      ) {
        aggregate {
          count
        }
      }
      failed_count: manual_text_batch_manual_texts_aggregate(
        where: { _or: [{ status: { _nin: ${JSON.stringify([
          ...SUCCESS_SMS_STATUSES,
          ...SENDING_SMS_STATUSES,
        ])} } }, { direct_snd_status: { _nin: ${JSON.stringify([
  ...DIRECT_SND_SMS_SUCCESS_STATUSES,
  ...DIRECT_SND_SMS_SUBMITTED_STATUSES,
])} } }] }
      ) {
        aggregate {
          count
        }
      }
      canceled_count: manual_text_batch_manual_texts_aggregate(
        where: { _or: [{ status: { _eq: ${JSON.stringify(
          CANCELED_STATUS
        )} } }, { direct_snd_status: { _eq: ${JSON.stringify(
  CANCELED_DIRECT_SND_SMS_STATUS
)} } }] }
      ) {
        aggregate {
          count
        }
      }
    }
    tasks_task_age_ranges {
      task_age_range_id

      task_age_ranges_age_range {
        age_range_id
        name
        code
      }
    }
    tasks_task_sexes {
      task_sex_id
      task_sex_sex {
        sex_id
        name
        display_name
        code
      }
    }
    tasks_task_education_levels {
      task_edu_level_id
      task_education_levels_education_level {
        education_level_id
        description
      }
    }
    tasks_task_employments {
      task_employment_id
      task_employments_employment {
        employment_id
        name
        code
      }
    }
    tasks_task_marital_statuses {
      task_marital_status_id
      task_marital_statuses_marital_status {
        marital_status_id
        name
        code
      }
    }
    tasks_task_income_codes {
      task_inc_code_id
      task_income_codes_income_code {
        income_code_id
        name
        code
      }
    }
    tasks_task_parties {
      task_party_id
      task_parties_party {
        party_id
        code
      }
    }
    tasks_task_modeled_parties {
      task_modeled_party_id
      task_modeled_parties_modeled_party {
        modeled_party_id
        code
      }
    }
    tasks_task_primary_voter_likeliness {
      task_primary_voter_likeliness_id
      task_primary_voter_likeliness_voter_likeliness {
        likeliness_id
        name
      }
    }
    tasks_task_voter_general_likeliness {
      task_voter_general_likeliness_id
      task_voter_general_likeliness_voter_likeliness {
        likeliness_id
        name
      }
    }
    tasks_task_voting_methods {
      task_voting_method_id
      task_voting_method_voting_method {
        voting_method_id
        name
      }
    }
    tasks_user_tasks {
      user_task_id
      user_tasks_user {
        user_id
        first_name
        last_name
      }
    }
    tasks_task_groups {
      task_group_id
      task_groups_group {
        group_id
        name
        groups_user_groups {
          user_groups_user {
            user_id
            first_name
            last_name
            email
          }
        }
      }
    }
    # Counts only. These relations are unbounded (a task can target 50k+ voters), so they are never
    # selected as rows here — see GET_TASK for the one query that needs the underlying voter rows.
    votersTotal: tasks_voters_queries_aggregate {
      aggregate {
        count
      }
    }
    votersCompleted: tasks_voters_queries_aggregate(where: { completed: { _eq: true } }) {
      aggregate {
        count
      }
    }

    ${smsCount(
      'smsFinishedOr',
      eitherStatusIn(FINISHED_SMS_STATUSES, FINISHED_DIRECT_SND_SMS_STATUSES)
    )}
    ${smsCount(
      'smsSuccessfulOr',
      eitherStatusIn(BILLING_SUCCESS_SMS_STATUSES, DIRECT_SND_SMS_SUCCESS_STATUSES)
    )}
    ${smsCount(
      'smsCanceled',
      `{ direct_snd_status: { _eq: ${JSON.stringify(CANCELED_DIRECT_SND_SMS_STATUS)} } }`
    )}
    ${smsCount(
      'smsFinishedCoalesced',
      coalescedStatusIn([...FINISHED_SMS_STATUSES, ...FINISHED_DIRECT_SND_SMS_STATUSES])
    )}
    ${smsCount(
      'smsSuccessfulCoalesced',
      coalescedStatusIn([...BILLING_SUCCESS_SMS_STATUSES, ...DIRECT_SND_SMS_SUCCESS_STATUSES])
    )}
    ${smsCount(
      'smsSendingCoalesced',
      coalescedStatusIn([...BILLING_SENDING_SMS_STATUSES, ...DIRECT_SND_SMS_SUBMITTED_STATUSES])
    )}
    ${smsCount(
      'smsFailedCoalesced',
      coalescedStatusNotIn([
        ...BILLING_SUCCESS_SMS_STATUSES,
        ...BILLING_SENDING_SMS_STATUSES,
        ...DIRECT_SND_SMS_SUCCESS_STATUSES,
        ...DIRECT_SND_SMS_SUBMITTED_STATUSES,
      ])
    )}

    # NULL status must count as "not failed", matching !FAILED_SURVEY_STATUSES.includes(undefined).
    surveyNotFailed: task_sms_survey_responses_aggregate(
      where: { _or: [{ status: { _is_null: true } }, { status: { _nin: ${JSON.stringify(
        FAILED_SURVEY_STATUSES
      )} } }] }
    ) {
      aggregate {
        count
      }
    }
    surveySuccessful: task_sms_survey_responses_aggregate(
      where: { status: { _in: ${JSON.stringify(SUCCESS_SURVEY_STATUSES)} } }
    ) {
      aggregate {
        count
      }
    }
    surveyPending: task_sms_survey_responses_aggregate(
      where: { _or: [{ status: { _is_null: true } }, { status: { _eq: "" } }] }
    ) {
      aggregate {
        count
      }
    }
    surveyFailed: task_sms_survey_responses_aggregate(
      where: { status: { _in: ${JSON.stringify(FAILED_SURVEY_STATUSES)} } }
    ) {
      aggregate {
        count
      }
    }

    audioFinished: tasks_audio_task_histories_aggregate(
      where: { status: { _in: ${JSON.stringify(FINISHED_CALL_STATUSES)} } }
    ) {
      aggregate {
        count
      }
    }
    audioSuccessful: tasks_audio_task_histories_aggregate(
      where: { status: { _in: ${JSON.stringify(SUCCESS_CALL_STATUSES)} } }
    ) {
      aggregate {
        count
      }
    }
    audioUnanswered: tasks_audio_task_histories_aggregate(
      where: { status: { _in: ${JSON.stringify(UNANSWERED_CALL_STATUSES)} } }
    ) {
      aggregate {
        count
      }
    }
    audioFailed: tasks_audio_task_histories_aggregate(
      where: { status: { _in: ${JSON.stringify(FAILED_CALL_STATUSES)} } }
    ) {
      aggregate {
        count
      }
    }

    tasks_task_geofences {
      task_geofence_id
      task_id
      lat
      long
    }

    tasks_task_voting_history {
      task_voting_history_id
      elections
      operation
    }

    tasks_task_audiences {
      task_audience_id
      saved_audience_id
      audience_name
      state
      definition
      task_audiences_saved_voter_audience {
        id
        name
        state
      }
    }

    use_manual_voter_list

    tasks_task_question_banks {
      task_question_bank_org_question_bank {
        org_question_bank_id
      }
    }

    tasks_task_phone_reliabilities {
      task_phone_reliability_id
      task_phone_reliability_phone_reliability {
        phone_reliability_id
        name
      }
    }

    tasks_task_phone_numbers {
      task_phone_number_id
      org_phone_number_id
      twilio_id
      phone_number
    }

    tasks_task_new_mover_date_ranges {
      task_new_mover_date_range_id
      task_new_mover_date_ranges_new_mover_date_range {
        new_mover_date_range_id
        name
        code
      }
    }

    tasks_task_personas {
      task_persona_id
      task_personas_persona {
        personicx_lifestage_cluster
        name
      }
    }
    tasks_task_workflow {
      task_workflow_id
      task_id
      creation_status
      creation_execution_arn
      creation_started_at
      creation_completed_at
      creation_error
      voters_listed_count
      voters_assigned_count
      update_status
      update_execution_arn
      update_started_at
      update_completed_at
      update_error
    }

    tasks_task_modeled_religions {
      task_modeled_religion_id
      task_modeled_religions_modeled_religion {
        religion_id
        name
      }
    }

    tasks_task_modeled_ethnicities {
      task_modeled_ethnicity_id
      task_modeled_ethnicities_modeled_ethnicity {
        ethnicity_id
        name
      }
    }

    tasks_task_modeled_languages {
      task_modeled_language_id
      task_modeled_languages_modeled_language {
        language_id
        name
      }
    }

    tasks_task_media {
      url
      mime_type
      audio_duration
      file_name
      size
    }
  }
`;

export const GET_TASKS = gql`
  query GetTasks($query: ri_apps_prd_tasks_bool_exp) {
    rows: ri_apps_prd_tasks(where: $query, order_by: { name: asc }) {
      ...TaskFragment
      tasks_sub_tasks {
        task_id
        goal: tasks_voters_queries_aggregate {
          aggregate {
            count
          }
        }
        completed: tasks_voters_queries_aggregate(where: { completed: { _eq: true } }) {
          aggregate {
            count
          }
        }
      }
    }
  }
  ${TaskFragment}
`;

export const GET_TASK_IDS = gql`
  query GetTasks($query: ri_apps_prd_tasks_bool_exp) {
    rows: ri_apps_prd_tasks(where: $query, order_by: { name: asc }) {
      task_id
      name
    }
  }
`;

export const GET_TASKS_FOR_REPORT = gql`
  query GetTasksForReport($query: ri_apps_prd_tasks_bool_exp, $limit: Int, $offset: Int) {
    rows: ri_apps_prd_tasks(where: $query, limit: $limit, offset: $offset) {
      ...TaskFragment
    }
    aggregate: ri_apps_prd_tasks_aggregate(where: $query) {
      aggregate {
        count
      }
    }
  }
  ${TaskFragment}
`;

// A voting area only carries state_id when it is state-scoped. Selecting every
// column that implies a state lets resolveTaskState() derive one for tasks
// targeted purely by precinct, county or district. See src/utils/task-helper.js.
export const VotingAreaStateSources = gql`
  fragment VotingAreaStateSources on ri_apps_prd_voting_areas {
    voting_area_id
    state_id
    precinct_id
    voting_areas_precinct {
      state_id
    }
    city_council_id
    voting_areas_city_council {
      state_id
    }
    city_id
    voting_areas_city {
      state_id
    }
    county_id
    voting_areas_county {
      state_id
    }
    state_leg_lower_sub_district_id
    voting_areas_state_leg_lower_sub_district {
      state_id
    }
    state_leg_lower_district_id
    voting_areas_state_leg_lower_district {
      state_id
    }
    state_leg_upper_district_id
    voting_areas_state_leg_upper_district {
      state_id
    }
    congressional_district_id
    voting_areas_congressional_district {
      state_id
    }
  }
`;

export const GET_TASK_STATE = gql`
  query GetTaskState($id: Int!) {
    ri_apps_prd_tasks(where: { task_id: { _eq: $id } }) {
      task_id
      tasks_task_voting_areas {
        task_voting_areas_voting_area {
          ...VotingAreaStateSources
        }
      }
    }
    ri_apps_prd_states {
      state_id
      short_name
    }
  }
  ${VotingAreaStateSources}
`;

export const GET_TASKS_FOR_CAMPAIGNS = gql`
  query GetTasks($campaign_id: Int!) {
    rows: ri_apps_prd_tasks(
      where: {
        tasks_task_campaigns: { task_campaigns_campaign: { campaign_id: { _eq: $campaign_id } } }
        bulk_task_id: { _is_null: true }
        #_or: [{ is_archived: { _eq: false } }, { is_archived: { _is_null: true } }]
      }
      order_by: { name: asc }
    ) {
      ...TaskFragment
      tasks_sub_tasks {
        task_id
        goal: tasks_voters_queries_aggregate {
          aggregate {
            count
          }
        }
        completed: tasks_voters_queries_aggregate(where: { completed: { _eq: true } }) {
          aggregate {
            count
          }
        }
      }
    }
  }
  ${TaskFragment}
`;

export const GET_TASK = gql`
  query GetTask($id: Int!) {
    row: ri_apps_prd_tasks_by_pk(task_id: $id) {
      ...TaskFragment
      # The edit screen diffs these against the newly selected voters, so it needs the ids, not a count.
      # Safe here because this query loads a single task; the list queries use TaskFragment's aggregates.
      tasks_voters_queries {
        voter_query_id
        voter_id
        completed
      }
      tasks_task_phone_numbers {
        task_phone_numbers_org_phone_number {
          purpose_code
        }
      }
      #campaign {
      #  id
      #}
      is_bulk_task
      bulk_task_id
      bulk_task_split_config
      tasks_sub_tasks {
        task_id
      }
      tasks_task_voting_areas {
        task_voting_area_id
        voting_area_id

        task_voting_areas_voting_area {
          ...VotingAreaFields
        }
      }
      tasks_user_tasks {
        user_task_id
        task_id

        user_tasks_user {
          user_id
          first_name
          last_name
        }
      }
      tasks_task_tags {
        task_tag_id
        task_id
        c_tag_id
        org_tag_id
        task_tags_c_tag {
          c_tag_id
          title
        }
        task_tags_org_tag {
          org_tag_id
          title
        }
      }
      tasks_tasks_event_statuses {
        tasks_event_statuses_event_status {
          event_status_id
          code
          name
        }
      }
      tasks_task_filter_modes {
        task_filter_mode_id
        field
        filter_mode
      }
      task_stripe_checkout_sessions {
        stripe_checkout_session_id
        payment_status
        url
      }
      assignment_config
    }
  }
  ${TaskFragment}
  ${VotingAreaFields}
`;

export const CREATE_TASK = gql`
  mutation CreateTask($object: ri_apps_prd_tasks_insert_input!) {
    row: insert_ri_apps_prd_tasks_one(object: $object) {
      ...TaskFragment
    }
  }
  ${TaskFragment}
`;

export const UPDATE_TASK = gql`
  mutation UpdateTask($id: Int!, $object: ri_apps_prd_tasks_set_input!) {
    row: update_ri_apps_prd_tasks_by_pk(pk_columns: { task_id: $id }, _set: $object) {
      ...TaskFragment
    }
  }
  ${TaskFragment}
`;

export const UPDATE_CHILD_TASKS = gql`
  mutation UpdateChildTasks($bulk_task_id: Int!, $object: ri_apps_prd_tasks_set_input!) {
    rows: update_ri_apps_prd_tasks(
      where: { bulk_task_id: { _eq: $bulk_task_id } }
      _set: $object
    ) {
      affected_rows
    }
  }
`;

export const CREATE_TASK_PHONE_NUMBERS = gql`
  mutation createTaskPhoneNumbers($data: [ri_apps_prd_task_phone_numbers_insert_input!]!) {
    rows: insert_ri_apps_prd_task_phone_numbers(objects: $data) {
      affected_rows
    }
  }
`;

export const DELETE_TASK_PHONE_NUMBERS = gql`
  mutation deleteTaskPhoneNumbers($id: [Int!]!) {
    rows: delete_ri_apps_prd_task_phone_numbers(where: { task_phone_number_id: { _in: $id } }) {
      affected_rows
    }
  }
`;

// Replaces the caller numbers of every listed task (a bulk task and its subtasks) in one
// transaction, leaving the subtasks themselves — and their progress — untouched.
export const REPLACE_TASK_PHONE_NUMBERS = gql`
  mutation replaceTaskPhoneNumbers(
    $taskIds: [Int!]!
    $objects: [ri_apps_prd_task_phone_numbers_insert_input!]!
  ) {
    delete_ri_apps_prd_task_phone_numbers(where: { task_id: { _in: $taskIds } }) {
      affected_rows
    }
    insert_ri_apps_prd_task_phone_numbers(objects: $objects) {
      affected_rows
    }
  }
`;

export const UPDATE_TASK_TAGS = gql`
  mutation UpdateTaskTags($id: Int, $objects: [ri_apps_prd_task_tags_insert_input]!) {
    delete_ri_apps_prd_task_tags(where: { task_id: { _eq: $id } }) {
      affected_rows
    }

    insert_ri_apps_prd_task_tags(objects: $objects) {
      affected_rows
    }
  }
`;

export const UPSERT_TASK_ASSIGNMENTS = gql`
  mutation UpsertTaskAssignments($data: [ri_apps_prd_user_tasks_insert_input!]!) {
    rows: insert_ri_apps_prd_user_tasks(
      objects: $data
      on_conflict: { constraint: user_tasks_pkey, update_columns: [user_id] }
    ) {
      affected_rows
    }
  }
`;

export const DELETE_TASK_ASSIGNMENTS = gql`
  mutation DeleteTaskAssignments($taskId: Int!, $userIds: [Int!]!) {
    rows: delete_ri_apps_prd_user_tasks(
      where: { task_id: { _eq: $taskId }, user_id: { _in: $userIds } }
    ) {
      affected_rows
    }
  }
`;

export const DELETE_TASK = gql`
  mutation DeleteTask($id: Int!) {
    row: delete_ri_apps_prd_tasks_by_pk(task_id: $id) {
      task_id
    }
  }
`;

export const DEACTIVATE_TASK = gql`
  mutation DeactivateTask($id: Int!) {
    row: update_ri_apps_prd_tasks_by_pk(pk_columns: { task_id: $id }, _set: { is_archived: true }) {
      task_id
    }
  }
`;

export const REACTIVATE_TASK = gql`
  mutation ReactivateTask($id: Int!) {
    row: update_ri_apps_prd_tasks_by_pk(
      pk_columns: { task_id: $id }
      _set: { is_archived: false }
    ) {
      task_id
    }
  }
`;

export const GET_TASKS_FOR_MAP = gql`
  query GetTasksForMap($organization_id: Int!) {
    rows: ri_apps_prd_tasks(
      where: {
        tasks_task_campaigns: {
          task_campaigns_campaign: { organization_id: { _eq: $organization_id } }
        }
        _or: [{ is_archived: { _eq: false } }, { is_archived: { _is_null: true } }]
      }
      order_by: { name: asc }
    ) {
      task_id
      name
      tasks_task_types {
        task_types_t_type {
          code
          t_type_id
          name
        }
      }
      tasks_task_campaigns {
        task_campaigns_campaign {
          campaign_id
        }
      }
    }
  }
`;

/**
 * Active (not archived, not paused, within its date window) walking/calling
 * tasks for an org, with each task's precinct (via tasks_task_voting_areas -
 * a task can have multiple voting-area rows, e.g. a general state/county
 * scope plus a precinct-level division; only rows with a non-null
 * voting_areas_precinct identify a single precinct) and goal/completed
 * counts (cheap Hasura aggregates, same pattern as GET_TASK_DIVISIONS).
 * Powers Daily Brief's "Field targets" card - real task progress per
 * precinct, not a computed swing/turnout ranking.
 */
export const GET_ACTIVE_FIELD_TASKS_BY_PRECINCT = gql`
  query GetActiveFieldTasksByPrecinct($organization_id: Int!, $today: date!) {
    rows: ri_apps_prd_tasks(
      where: {
        tasks_task_campaigns: {
          task_campaigns_campaign: { organization_id: { _eq: $organization_id } }
        }
        _and: [
          { _or: [{ is_archived: { _eq: false } }, { is_archived: { _is_null: true } }] }
          { _or: [{ is_paused: { _eq: false } }, { is_paused: { _is_null: true } }] }
          { _or: [{ start_date: { _lte: $today } }, { start_date: { _is_null: true } }] }
          { _or: [{ end_date: { _gte: $today } }, { end_date: { _is_null: true } }] }
        ]
        tasks_task_types: { task_types_t_type: { code: { _in: ["walking", "calling"] } } }
      }
    ) {
      task_id
      tasks_task_types {
        task_types_t_type {
          code
        }
      }
      tasks_task_voting_areas {
        task_voting_areas_voting_area {
          voting_areas_precinct {
            name
          }
        }
      }
      tasks_voters_queries_aggregate {
        aggregate {
          count
        }
      }
      tasks_voters_queries_completed_aggregate: tasks_voters_queries_aggregate(
        where: { completed: { _eq: true } }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`;

export const GET_PRECEDING_TASK_FOR_FOLLOW_UP_BY_ID = gql`
  query GetTaskForFollowUpById($id: Int!) {
    row: ri_apps_prd_tasks_by_pk(task_id: $id) {
      task_id
      first_parent_task_id
    }
  }
`;

export const GET_TASK_WORKFLOW = gql`
  query GetTaskWorkflow($task_id: Int!) {
    ri_apps_prd_task_workflow(where: { task_id: { _eq: $task_id } }) {
      task_workflow_id
      task_id
      creation_status
      creation_execution_arn
      creation_started_at
      creation_completed_at
      creation_error
      voters_listed_count
      voters_assigned_count
      update_status
      update_execution_arn
      update_started_at
      update_completed_at
      update_error
      created_at
      updated_at
      voters_total_count
      sub_tasks_count
    }
  }
`;

export const GET_TASK_ASSIGNMENTS_SUMMARY = gql`
  query GetTaskAssignmentsSummary($task_id: Int!) {
    summary_by_voters: ri_apps_prd_user_tasks(where: { task_id: { _eq: $task_id } }) {
      user_id
      user_tasks_assignments_aggregate {
        aggregate {
          count
        }
      }
    }
    summary_by_households: ri_apps_prd_user_tasks(where: { task_id: { _eq: $task_id } }) {
      user_id
      user_tasks_assignments_aggregate(distinct_on: [household_id]) {
        aggregate {
          count
        }
      }
    }
    group_summary_by_voters: ri_apps_prd_user_groups(
      where: { user_groups_assignments: { task_id: { _eq: $task_id } } }
    ) {
      user_id
      user_groups_assignments_aggregate(where: { task_id: { _eq: $task_id } }) {
        aggregate {
          count
        }
      }
    }
    group_summary_by_households: ri_apps_prd_user_groups(
      where: { user_groups_assignments: { task_id: { _eq: $task_id } } }
    ) {
      user_id
      user_groups_assignments_aggregate(
        distinct_on: [household_id]
        where: { task_id: { _eq: $task_id } }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`;

export const GET_TASK_DIVISIONS = gql`
  query GetTaskDivisions($task_id: Int!) {
    ri_apps_prd_tasks(where: { bulk_task_id: { _eq: $task_id } }) {
      task_id
      name
      start_date
      end_date
      is_archived
      tasks_task_types {
        task_type_id
        task_types_t_type {
          t_type_id
          name
          code
        }
      }
      # Fetch ONLY essential fields for status tracking (completed/remaining)
      # This excludes heavy fields like reg_lat, reg_long, voter_id, assigned_to, in_progress
      tasks_voters_queries {
        voter_query_id
        household_id
        completed
      }
      # Use aggregation for total voter count
      tasks_voters_queries_aggregate {
        aggregate {
          count
        }
      }
      # Completed voters count
      tasks_voters_queries_completed_aggregate: tasks_voters_queries_aggregate(
        where: { completed: { _eq: true } }
      ) {
        aggregate {
          count
        }
      }
      # Only fetch minimal coordinate data for map visualization (walking tasks)
      # Limit to reasonable sample for map rendering (500 points should be enough)
      tasks_voters_queries_for_map: tasks_voters_queries(
        where: {
          reg_lat: { _is_null: false }
          reg_long: { _is_null: false }
          is_outlier_home: { _eq: false }
        }
        distinct_on: household_id
        limit: 100
      ) {
        household_id
        reg_lat
        reg_long
      }
      tasks_task_subdivision_boundaries {
        task_id
        boundaries
        area_description
      }
      tasks_user_tasks {
        user_tasks_user {
          user_id
          first_name
          last_name
          email
        }
      }
      tasks_task_groups {
        task_group_id
        task_groups_group {
          group_id
          name
        }
      }
    }
  }
`;

export const SEARCH_DIRECT_SND_CAMPAIGNS = gql`
  query SearchDirectSndCampaigns($where: ri_apps_prd_direct_snd_campaigns_bool_exp!) {
    rows: ri_apps_prd_direct_snd_campaigns(where: $where) {
      direct_snd_campaign_id
      task_id
      manual_text_batch_id
    }
  }
`;

export const DELETE_TASK_MEDIA = gql`
  mutation DeleteTaskMedia($taskId: Int!) {
    delete_ri_apps_prd_task_media(where: { task_id: { _eq: $taskId } }) {
      returning {
        task_media_id
        url
      }
    }
  }
`;

export const CREATE_TASK_MEDIA = gql`
  mutation CreateTaskMedia($data: ri_apps_prd_task_media_insert_input!) {
    insert_ri_apps_prd_task_media_one(object: $data) {
      task_media_id
      url
      file_name
      audio_duration
      mime_type
      size
    }
  }
`;

export const DELETE_TASK_MEDIA_FOR_TASKS = gql`
  mutation DeleteTaskMediaForTasks($taskIds: [Int!]!) {
    delete_ri_apps_prd_task_media(where: { task_id: { _in: $taskIds } }) {
      returning {
        task_media_id
      }
    }
  }
`;

export const CREATE_TASK_MEDIA_BULK = gql`
  mutation CreateTaskMediaBulk($objects: [ri_apps_prd_task_media_insert_input!]!) {
    insert_ri_apps_prd_task_media(objects: $objects) {
      returning {
        task_media_id
        task_id
      }
    }
  }
`;

// Matches a task assigned directly or through a group, and relies on server-side aggregates so
// the list stays O(tasks).
export const GET_ASSIGNED_TASKS = gql`
  query GetAssignedTasks($user_id: Int!, $organization_condition: ri_apps_prd_tasks_bool_exp = {}) {
    ri_apps_prd_tasks(
      where: {
        _and: [
          $organization_condition
          {
            _or: [
              { tasks_user_tasks: { user_id: { _eq: $user_id } } }
              {
                tasks_task_groups: {
                  task_groups_group: { groups_user_groups: { user_id: { _eq: $user_id } } }
                }
              }
            ]
          }
          { _or: [{ is_archived: { _is_null: true } }, { is_archived: { _eq: false } }] }
        ]
      }
      order_by: [{ name: asc }]
    ) {
      task_id
      name
      start_date
      end_date
      tasks_task_types {
        task_types_t_type {
          t_type_id
          name
        }
      }
      tasks_task_campaigns {
        task_campaigns_campaign {
          campaign_id
          name
          campaigns_organization {
            organization_id
          }
          campaigns_campaign_tags {
            c_tag_id
            campaign_tags_c_tag {
              title
            }
            o_tag_id
            campaign_tags_org_tag {
              title
              organization_id
            }
          }
        }
      }
      tasks_task_voting_areas {
        task_voting_areas_voting_area {
          ...VotingAreaStateSources
        }
      }
      totalVoters: tasks_voters_queries_aggregate {
        aggregate {
          count
        }
      }
      completedVoters: tasks_voters_queries_aggregate(where: { completed: { _eq: true } }) {
        aggregate {
          count
        }
      }
    }
    ri_apps_prd_states {
      state_id
      short_name
    }
  }
  ${VotingAreaStateSources}
`;

// The calling script, question bank and media are deliberately absent from GET_ASSIGNED_TASKS
// and fetched separately here, so a manager attaching a question bank shows up without a
// task-list refresh.
export const GET_ASSIGNED_TASK_DETAILS = gql`
  query GetAssignedTaskDetails($task_id: Int!) {
    ri_apps_prd_tasks(where: { task_id: { _eq: $task_id } }) {
      task_id
      calling_script
      tasks_task_question_banks {
        task_question_bank_org_question_bank {
          org_question_bank_id
          name
          org_question_banks_question_bank_filters {
            question_bank_filter_id
            filter_field
            filter_value
            question_set_id
          }
          org_question_banks_question_bank_question_sets {
            question_bank_question_set_id
            question_bank_question_sets_question_bank_objects {
              question_set_object_id
              type
              text
              parent
              tag_id
              org_tag_id
              dynamic_config
              variant_slot
            }
          }
        }
      }
      tasks_task_media {
        url
        mime_type
        audio_duration
      }
    }
  }
`;

export const GET_TASK_VOTER_COUNTS = gql`
  query GetTaskVoterCounts($task_id: Int!) {
    ri_apps_prd_tasks(where: { task_id: { _eq: $task_id } }) {
      task_id
      totalVoters: tasks_voters_queries_aggregate {
        aggregate {
          count
        }
      }
      completedVoters: tasks_voters_queries_aggregate(where: { completed: { _eq: true } }) {
        aggregate {
          count
        }
      }
    }
  }
`;

// Cursored on (updated_at, voter_query_id) so a redelivery after a reconnect does not replay
// the whole task.
export const GET_UPDATED_VOTER_QUERIES_STREAM = gql`
  subscription GetUpdatedVoterQueries(
    $task_id: Int!
    $cursor_updated_at: timestamptz!
    $cursor_voter_query_id: Int!
  ) {
    ri_apps_prd_voters_queries_stream(
      where: {
        task_id: { _eq: $task_id }
        _or: [
          { updated_at: { _gt: $cursor_updated_at } }
          {
            _and: [
              { updated_at: { _eq: $cursor_updated_at } }
              { voter_query_id: { _gt: $cursor_voter_query_id } }
            ]
          }
        ]
      }
      cursor: { initial_value: { updated_at: $cursor_updated_at }, ordering: ASC }
      batch_size: 10
    ) {
      voter_query_id
      voter_id
      updated_at
      in_progress
      in_progress_at
      in_progress_by_user_id
      completed
    }
  }
`;

export const UPDATE_VOTER_QUERY_IN_PROGRESS = gql`
  mutation UpdateVoterQueryInProgress($voter_query_id: Int!, $user_id: Int!) {
    update_ri_apps_prd_voters_queries_by_pk(
      pk_columns: { voter_query_id: $voter_query_id }
      _set: { in_progress: true, in_progress_by_user_id: $user_id, in_progress_at: "now()" }
    ) {
      voter_query_id
      completed
    }
  }
`;

export const UPDATE_VOTER_QUERY_COMPLETED = gql`
  mutation UpdateVoterQueryCompleted($voter_query_id: Int!, $completed: Boolean!) {
    update_ri_apps_prd_voters_queries_by_pk(
      pk_columns: { voter_query_id: $voter_query_id }
      _set: {
        completed: $completed
        in_progress: false
        in_progress_at: null
        in_progress_by_user_id: null
      }
    ) {
      voter_query_id
      completed
    }
  }
`;

/**
 * Complete a voter's row in a task by what it describes rather than by its primary key. Ported
 * from right-impact-app e207033.
 *
 * Changing a task's scope deletes and recreates every voters_queries row that is not already
 * completed, so a voter_query_id captured before that change addresses a row the server has
 * forgotten. The call console holds voter_query_id from when its queue loaded, so a manager
 * editing the task mid-session would make a by_pk completion update nothing -- and report
 * success. The result would be written, the voter never completed, and they would be offered and
 * dialled again.
 *
 * (task_id, voter_id) survives the recreation, so the completion lands on whichever row now
 * represents that voter. affected_rows tells the caller whether it found one.
 */
export const UPDATE_VOTER_QUERY_COMPLETED_BY_VOTER = gql`
  mutation UpdateVoterQueryCompletedByVoter($task_id: Int!, $voter_id: Int!, $completed: Boolean!) {
    update_ri_apps_prd_voters_queries(
      where: { task_id: { _eq: $task_id }, voter_id: { _eq: $voter_id } }
      _set: {
        completed: $completed
        in_progress: false
        in_progress_at: null
        in_progress_by_user_id: null
      }
    ) {
      affected_rows
    }
  }
`;

export const CREATE_TASK_RESULT = gql`
  mutation InsertTResults(
    $created_date: timestamptz
    $user_id: Int
    $voter_id: Int
    $notes: String
    $event_status_id: Int!
    $organization_id: Int
    $captured_lat: numeric
    $captured_lng: numeric
  ) {
    insert_ri_apps_prd_t_results(
      objects: {
        created_date: $created_date
        user_id: $user_id
        voter_id: $voter_id
        notes: $notes
        event_status_id: $event_status_id
        organization_id: $organization_id
        captured_lat: $captured_lat
        captured_lng: $captured_lng
      }
    ) {
      affected_rows
      returning {
        t_result_id
      }
    }
  }
`;

export const INSERT_TASK_RESULT = gql`
  mutation InsertTaskResults($task_id: Int, $t_result_id: Int) {
    insert_ri_apps_prd_task_results(objects: { task_id: $task_id, t_result_id: $t_result_id }) {
      affected_rows
      returning {
        id
        task_id
        t_result_id
      }
    }
  }
`;

export const INSERT_TASK_RESULT_TAGS = gql`
  mutation InsertTaskResultTags($data: [ri_apps_prd_t_result_tags_insert_input!]!) {
    insert_ri_apps_prd_t_result_tags(objects: $data) {
      affected_rows
    }
  }
`;

// A repeat knock re-adds a tag the voter already has; on_conflict makes that a no-op instead of a
// uniqueness error. One key per tag kind, so one mutation per kind.
export const INSERT_VOTER_C_TAG = gql`
  mutation InsertVoterCTag($data: ri_apps_prd_org_voter_tags_insert_input!) {
    insert_ri_apps_prd_org_voter_tags(
      objects: [$data]
      on_conflict: {
        constraint: org_voter_tags_voter_id_organization_id_c_tag_id_key
        update_columns: []
      }
    ) {
      affected_rows
    }
  }
`;

export const INSERT_VOTER_ORG_TAG = gql`
  mutation InsertVoterOrgTag($data: ri_apps_prd_org_voter_tags_insert_input!) {
    insert_ri_apps_prd_org_voter_tags(
      objects: [$data]
      on_conflict: {
        constraint: org_voter_tags_voter_id_organization_id_org_tag_id_key
        update_columns: []
      }
    ) {
      affected_rows
    }
  }
`;

export const INSERT_TASK_QUESTION_BANK_RESULT = gql`
  mutation InsertTaskQuestionBankResult($data: ri_apps_prd_t_question_bank_results_insert_input!) {
    row: insert_ri_apps_prd_t_question_bank_results_one(object: $data) {
      t_question_bank_result_id
    }
  }
`;

export const INSERT_TASK_QUESTION_BANK_ANSWERS = gql`
  mutation InsertTaskQuestionBankAnswers(
    $data: [ri_apps_prd_t_question_bank_result_answers_insert_input!]!
  ) {
    row: insert_ri_apps_prd_t_question_bank_result_answers(objects: $data) {
      affected_rows
    }
  }
`;

export const GET_ASSIGNED_TASK_PHONE_NUMBERS = gql`
  query GetAssignedTaskPhoneNumbers($taskId: Int!) {
    rows: ri_apps_prd_task_phone_numbers(where: { task_id: { _eq: $taskId } }) {
      task_phone_number_id
      phone_number
      twilio_id
      org_phone_number_id
    }
  }
`;
